import { FC, useCallback, useEffect, useRef, useState } from 'react';
import './AdminStudents.scss';
import TabsContentHeader from '../../components/AdminDashboardTabsContentHeader/TabsContentHeader';
import { CLASSROOMS_FOR_STUDENTS } from './api/AdminStudentsQuery';
import { useLazyQuery } from '@apollo/client';
import { debounce } from 'lodash';
import images from '../../assets/images';
import Loader from '../../components/Loader/Loader';
import { CircularProgress, Tooltip } from '@mui/material';
import './AdminStudentsClassroom.scss';
import AdminStudents from './AdminStudents';
import { formatClassroomStudentsCount, formatStudentPupilsCount, getScreenDimensions } from '../../utils/helper';
import { FETCH_POLICY_NO_CACHE } from '../../utils/constants';
import InfiniteScroll from 'react-infinite-scroll-component';

export interface ClassroomObjectType {
    cCode: string;
    cId: string;
    cName: string;
    cOriginalName: string;
    cStudentsCount: number;
    classroomSubjectsByCId: {
        nodes: Subjects[]
    }
    studentClassroomsByCId:{
        nodes: StudentsObjectType[];
        totalCount: number;
    }
}
export interface StudentsObjectType{
    sId:string;
    studentBySId :{
            userByUId :{
              uFullName:string
            }
          }
}
interface Subjects {
    subjectBySjId: {
        sjName: string;
        sjOriginalName: string;
    }
}
interface ClassroomListType {
    allClassrooms: {
        nodes: ClassroomObjectType[];
        totalCount: number;
    }
    allStudentClassrooms:{
        totalCount:number;
    }
}

type AdminStudentsType = {
    isFullView: boolean;
    setisFullView: (value: boolean) => void;
    setisSearchClick: (value: boolean) => void;
    isSearchClick: boolean;
}

const AdminStudentsClassroom: FC<AdminStudentsType> = ({ isFullView, setisFullView, setisSearchClick, isSearchClick }) => {
    const { userImage, searchCloseIcon } = images;
    const [displayStudents, setDisplayStudents] = useState<boolean>(false);
    const [displayStudentForm, setDisplayStudentForm] = useState<boolean>(false);
    const [studentClassroomsList, setStudentClassroomsList] = useState<ClassroomListType>();
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState("");
    const classNameRefs = useRef<(HTMLLabelElement | null)[]>([]);
    const [isOverflow, setIsOverflow] = useState<boolean[]>([]);
    const [selectedClass, setSelectedClass] = useState<ClassroomObjectType>();
    const { isMobileScreen } = getScreenDimensions();
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 50;
    
    const [getStudentClassrooms] = useLazyQuery(CLASSROOMS_FOR_STUDENTS, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setStudentClassroomsList(prev => {
                if (!prev || offset === 0) {
                    return data;
                }
                // Merge new nodes with previous nodes for infinite scroll
                return {
                    ...data,
                    allClassrooms: {
                        ...data.allClassrooms,
                        nodes: [
                            ...(prev.allClassrooms?.nodes || []),
                            ...(data.allClassrooms?.nodes || [])
                        ]
                    },
                    allStudentClassrooms: data.allStudentClassrooms
                };
            });
            if (data?.allClassrooms?.nodes) {
                setHasMore(data.allClassrooms.nodes.length === limit && (offset + limit) < (data.allClassrooms.totalCount || 0));
            }
            setRefetchData(false);
        }
    });
    
    useEffect(() => {
        setOffset(0);
        setHasMore(true);
        getStudentClassrooms({
            variables: {
                orderBy: ["C_NAME_ASC"],
                searchInput: '%%',
                limit,
                offset: 0
            }
        });
    }, []);

    useEffect(() => {
        if (refetchData === true || !displayStudents) {
            setOffset(0);
            setHasMore(true);
            getStudentClassrooms({
                variables: {
                    orderBy: ["C_NAME_ASC"],
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            });
        }
    }, [refetchData, displayStudents]);

    //To search classrooms of students
    const handleSearchDebounce = useCallback(
        debounce((value) => {
            setSearchValue(value);
            setOffset(0);
            if(value.trim() === ""){
                setStudentClassroomsList(undefined);
            }
            if(value?.trim().length >= 3 || value.trim() === ""){
                getStudentClassrooms({
                    variables: {
                        orderBy: ["C_NAME_ASC"],
                        searchInput: `%${value.trim()}%`,
                        limit,
                        offset: 0
                    }
                });
            }
        }, 500),
        [getStudentClassrooms]
    );
    
        // Infinite scroll fetch more
    const fetchMoreStudents = () => {
        const newOffset = offset + limit;
        setOffset(newOffset);
        getStudentClassrooms({
            variables: {
                orderBy: ["C_NAME_ASC"],
                searchInput: `%${searchValue.trim()}%`,
                limit,
                offset: newOffset
            }
        });
    };

    //To check the classroomname is overflow
    const checkOverflow = (index: number) => {
        if (classNameRefs.current[index]) {
            const overflow = classNameRefs.current[index]!.scrollWidth > classNameRefs.current[index]!.clientWidth;
            setIsOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

    //To check overflow in resize 
    useEffect(() => {
        // Check overflow for all classrooms when the data is loaded
        if (studentClassroomsList?.allClassrooms?.nodes) {
            studentClassroomsList.allClassrooms.nodes.forEach((_, index) => {
                checkOverflow(index); // Check overflow for each classroom name
            });
        }

        const handleResize = () => {
            studentClassroomsList?.allClassrooms?.nodes.forEach((_, index) => {
                checkOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [studentClassroomsList]);



    //To get the classroom details by id
    const getClassroomStudentsDetails = (cardItem: ClassroomObjectType) => {
        setSelectedClass(cardItem);
        setDisplayStudents(true);
    };

    useEffect(() => {
        setisFullView(displayStudents);
    }, [displayStudents])

    const handleCloseSearch = () => {
        setisSearchClick(false);
        setSearchValue("");
        handleSearchDebounce("");
    };

    const searchOnChange = (event: { target: { value: string; }; }) => {
        setSearchValue(event.target.value);
        if (handleSearchDebounce){
          handleSearchDebounce(event?.target?.value);
        }
    };

    return (
        <>
            {
                !displayStudents ?
                    <div className='AdminStudentClassroom'>
                        {
                            (isMobileScreen && isSearchClick) ?
                            <div className={'adminStudentSearch'}>
                                    <input id="searchBox" type='text' placeholder='Search' onChange={searchOnChange} value={searchValue} className='SearchInputFieldStyle' />
                                    {(isMobileScreen && isSearchClick) && <img src={searchCloseIcon} alt='closeIcon' className='closeIcon' onClick={handleCloseSearch} />}
                            </div>
                            :
                            <TabsContentHeader
                                title={"All Students"}
                                subtitle={formatClassroomStudentsCount(studentClassroomsList?.allStudentClassrooms?.totalCount || 0)}
                                addLabel={""}
                                setDisplayForm={setDisplayStudentForm}
                                displayForm={displayStudentForm}
                                subImage={userImage}
                                handleSearchDebounce={handleSearchDebounce}
                                searchValue={searchValue}
                                setSearchValue={setSearchValue}
                            />
                        }
                        {
                            (studentClassroomsList === undefined) ? <Loader bottom='145px' />
                                :
                                (   
                                    <InfiniteScroll
                                        dataLength={studentClassroomsList?.allClassrooms?.nodes?.length || 0}
                                        next={fetchMoreStudents}
                                        hasMore={hasMore}
                                        loader={
                                            <div className="scroll-loader">
                                                <CircularProgress size={30} />
                                            </div>
                                        }
                                        height={`100%`}
                                        className={`${studentClassroomsList?.allClassrooms?.totalCount ? 'studentClassroomListContainer' : 'forNoDataFound'}`}
                                    >                                 
                                            {
                                                studentClassroomsList?.allClassrooms?.totalCount ?
                                                    studentClassroomsList?.allClassrooms?.nodes?.map((cardItem: ClassroomObjectType, index: number) => {
                                                        return (
                                                            <div 
                                                                className='studentClassCardContainer' 
                                                                key={`${index}-${cardItem?.cId}`} 
                                                                onClick={() => getClassroomStudentsDetails(cardItem)}
                                                            >
                                                                <div className='topSectionNameStudent'>
                                                                    <Tooltip
                                                                        title={cardItem?.cOriginalName}
                                                                        disableHoverListener={!isOverflow[index]}
                                                                        placement="top"
                                                                    >
                                                                        <label 
                                                                            className='classRoomNameStyleStudent' 
                                                                            ref={(el) => (classNameRefs.current[index] = el)} 
                                                                            onMouseEnter={() => checkOverflow(index)}
                                                                        >
                                                                            {cardItem?.cOriginalName}
                                                                        </label>
                                                                    </Tooltip>
                                                                </div>
                                                                <div className='noOfPeoplesStudent'>
                                                                    {formatStudentPupilsCount(cardItem?.cStudentsCount || 0,cardItem?.studentClassroomsByCId?.totalCount || 0)} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                                                </div>
                                                            </div>
                                                        )
                                                    })
                                                    :
                                                    <div className='noDataFoundStyle'>No data found</div>
                                            }
                                    </InfiniteScroll>   
                                )
                        }
                    </div> :
                    <>
                       <AdminStudents
                          setDisplayStudents={setDisplayStudents}
                          displayStudents={displayStudents}
                          selectedClass={selectedClass}
                          isFullView={isFullView}
                          setisFullView={setisFullView}
                          setisSearchClick={setisSearchClick}
                          isSearchClick={isSearchClick}
                       />
                    </>
            }
        </>
    )
}
export default AdminStudentsClassroom;
